import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { deleteAccount, verifyEmailOTP, verifyEmailPassword } from "../../api/auth";
import api from "../../api";
import { saveCompanyBranding } from "../../utils/companyBranding";
import { userEndpoints } from "../../api/endpoints/user.endpoints";
import Loader from "../Loader";
import { redirectToDigitalAfterLogout } from "../../utils/digitalBilling";
import { disconnectSocket } from "../../api/socket/socket";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const localToken = localStorage.getItem("token");
  const [token, setToken] = useState(localToken || "");
  const [isAuthenticated, setIsAuthenticated] = useState(!!localToken);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);
  const [isPageNotOTP, setIsPageNotOTP] = useState(true);

  const cleanSsoParamsFromUrl = useCallback(() => {
    const currentUrl = new URL(window.location.href);
    if (
      !currentUrl.searchParams.has("ssoToken") &&
      !currentUrl.searchParams.has("token") &&
      !currentUrl.searchParams.has("source")
    ) {
      return;
    }
    currentUrl.searchParams.delete("ssoToken");
    currentUrl.searchParams.delete("token");
    currentUrl.searchParams.delete("source");
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, "", nextUrl);
  }, []);

  const fetchUser = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const res = await api.get(userEndpoints.getUser);
      setUser(res.data);
      setIsAuthenticated(true);
      if (res.data?.companyLogo || res.data?.companyName) {
        saveCompanyBranding({
          companyLogo: res.data?.companyLogo,
          companyName: res.data?.companyName,
        });
      }
      return res.data;
    } catch (error) {
      console.log(error);
      if (!silent) {
        setIsAuthenticated(false);
        setUser(null);
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const handleSsoAutoLogin = useCallback(async () => {
    const currentUrl = new URL(window.location.href);
    const hasSsoParams =
      currentUrl.searchParams.has("ssoToken") || currentUrl.searchParams.has("token");
    const ssoToken =
      currentUrl.searchParams.get("ssoToken") ||
      currentUrl.searchParams.get("token") ||
      sessionStorage.getItem("pendingSsoToken") ||
      localStorage.getItem("ssoToken");
    if (!ssoToken) {
      return false;
    }

    // Fresh launch from Digital (?ssoToken=...) — drop stale SI session first.
    if (hasSsoParams) {
      localStorage.removeItem("token");
      setToken("");
      setIsAuthenticated(false);
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/sso-sync", { ssoToken });
      const payload = response?.data?.data;
      const newToken = payload?.token;
      if (newToken) {
        setToken(newToken);
        localStorage.setItem("token", newToken);
        localStorage.setItem("ssoToken", ssoToken);
        saveCompanyBranding({
          companyLogo: payload?.user?.companyLogo,
          companyName: payload?.user?.companyName,
        });
        setIsAuthenticated(true);
        await fetchUser();
        return true;
      }
      return false;
    } catch (error) {
      console.error("SSO auto-login failed:", error);
      if (hasSsoParams) {
        const backendMessage = error?.response?.data?.message || "";
        sessionStorage.setItem("ssoLoginError", backendMessage || "invalid_or_expired");
      }
      return false;
    } finally {
      sessionStorage.removeItem("pendingSsoToken");
      cleanSsoParamsFromUrl();
      setLoading(false);
    }
  }, [cleanSsoParamsFromUrl, fetchUser]);

  useEffect(() => {
    if (isAuthInitializing) return;
    if (token) {
      fetchUser();
      return;
    }
    setIsAuthenticated(false);
    setUser(null);
    setLoading(false);
  }, [token, isPageNotOTP, isAuthInitializing, fetchUser]);

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      await handleSsoAutoLogin();
      setIsAuthInitializing(false);
    };
    initAuth();
  }, [handleSsoAutoLogin]);

  useEffect(() => {
    if (!token || isAuthInitializing) return undefined;

    const refreshBrandingFromDigital = async () => {
      try {
        await fetchUser({ silent: true });
      } catch {
        // Ignore transient refresh failures when returning from Digital.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshBrandingFromDigital();
      }
    };

    window.addEventListener("focus", refreshBrandingFromDigital);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refreshBrandingFromDigital);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, isAuthInitializing, fetchUser]);

  async function verifyOtpWithBackend(data) {
    try {
      const authenticatedUser = await verifyEmailOTP(data);
      if (authenticatedUser?.data?.data?.token) {
        const newToken = authenticatedUser.data.data.token;
        setToken(newToken);
        localStorage.setItem("token", newToken);
        setIsAuthenticated(true);
        // await fetchUser(); // Ensure the user data is fetched
        return authenticatedUser;
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function verifyPasswordWithBackend(data) {
    try {
      const authenticatedUser = await verifyEmailPassword(data);
      if (authenticatedUser?.data?.data?.token) {
        const newToken = authenticatedUser.data.data.token;
        const ssoToken = authenticatedUser.data.data.ssoToken;
        setToken(newToken);
        localStorage.setItem("token", newToken);
        if (ssoToken) {
          localStorage.setItem("ssoToken", ssoToken);
        }
        setIsAuthenticated(true);
        await fetchUser(); // Ensure the user data is fetched
        setLoading(false);
        return authenticatedUser;
      }
    } catch (err) {
      setLoading(false);
      throw err;
    }
  }

  async function DeleteAccountFromBackend() {
    try {
      const response = await deleteAccount();
      if (response) {
      return response
      }
    } catch (err) {
      throw err;
    }
  }

  function LogOutUser() {
    disconnectSocket();
    redirectToDigitalAfterLogout();
  }

  const value = {
    verifyOtpWithBackend,
    verifyPasswordWithBackend,
    isAuthenticated,
    logout: LogOutUser,
    deleteAcc : DeleteAccountFromBackend,
    user,
    loading,
    isAuthInitializing,
    setIsAuthenticated,
    setIsPageNotOTP,
  };

  if (loading) return <Loader />;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
